import {
  AlertCircle,
  AlertTriangle,
  Clock3,
  LoaderCircle,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  Badge,
  Bubble,
  BubbleContent,
  Button,
  Message as OpenCloudMessage,
  MessageContent,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@opencloud/ui";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { AgentApiError, api } from "../api.js";
import { agentBrand } from "../brand.js";
import {
  projectChildAgentEvidence,
  type ChildAgentEvidenceTimelineItem,
} from "../child-agent-evidence.js";
import type {
  AgentTraceInspectionView,
  OpenCloudUIMessage,
} from "../types.js";
import { Markdown } from "./Markdown.js";
import { TypingIndicator } from "./RunActivity.js";
import {
  mergeInspectionPages,
  traceInspectionPath,
} from "./TraceInspector.js";
import { ToolActivityCard } from "./ToolActivityCard.js";
import { childAgentName, childStateIsLive } from "../child-agent-presentation.js";

type ChildPart = Extract<
  OpenCloudUIMessage["parts"][number],
  { type: "data-child" }
>;
// Root tasks and repairs use the same detail view without inventing lineage.
export type AgentDetailPart = Omit<ChildPart, "data"> & {
  data: Omit<ChildPart["data"], "parentRunId"> & { parentRunId: string | null };
};

export function ChildAgentPanel({
  appId,
  part,
  open,
  onOpenChange,
  stateLabel,
  stateDetail,
  returnFocus,
  summary,
}: {
  appId: string;
  part: AgentDetailPart;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stateLabel: string;
  stateDetail: string;
  returnFocus?: RefObject<HTMLButtonElement | null>;
  summary?: ReactNode;
}) {
  const [inspection, setInspection] =
    useState<AgentTraceInspectionView | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadVersion = useRef(0);
  const request = useRef<AbortController | null>(null);
  const live = childStateIsLive(part.data.state);

  const load = useCallback(
    async (quiet = false) => {
      // A refresh must not cancel a slower initial load or pagination walk.
      if (request.current && !request.current.signal.aborted) return;
      const version = ++loadVersion.current;
      const controller = new AbortController();
      request.current = controller;
      if (!quiet) {
        setLoading(true);
        setLoadingMore(false);
      }
      try {
        let page = await api<AgentTraceInspectionView>(
          traceInspectionPath(
            appId,
            part.data.parentRunId ?? part.data.runId,
            0,
            100,
            part.data.parentRunId ? part.data.runId : undefined,
          ),
          { signal: controller.signal },
        );
        if (version !== loadVersion.current) return;
        let combined = page;
        if (!quiet) setInspection(combined);
        setError(null);
        setLoading(false);

        while (page.window.nextOffset !== null) {
          const offset = page.window.nextOffset;
          setLoadingMore(true);
          page = await api<AgentTraceInspectionView>(
            traceInspectionPath(
              appId,
              part.data.parentRunId ?? part.data.runId,
              offset,
              100,
              part.data.parentRunId ? part.data.runId : undefined,
            ),
            { signal: controller.signal },
          );
          if (version !== loadVersion.current) return;
          combined = mergeInspectionPages(combined, page);
          if (!quiet) setInspection(combined);
          if (page.window.nextOffset !== null && page.window.nextOffset <= offset) {
            break;
          }
        }
        if (quiet) setInspection(combined);
      } catch (nextError) {
        if (controller.signal.aborted || version !== loadVersion.current) return;
        setError(childEvidenceError(nextError, live));
      } finally {
        if (request.current === controller) request.current = null;
        if (version === loadVersion.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [appId, live, part.data.parentRunId, part.data.runId],
  );

  useEffect(() => {
    if (!open) {
      loadVersion.current += 1;
      request.current?.abort();
      return;
    }
    setInspection(null);
    setError(null);
    void load();
    return () => {
      loadVersion.current += 1;
      request.current?.abort();
    };
  }, [load, open]);

  useEffect(() => {
    if (!open || !live) return;
    const interval = window.setInterval(() => void load(true), 3_000);
    return () => window.clearInterval(interval);
  }, [live, load, open]);

  const purpose = childAgentName(part.data.purpose);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="a3-theme a3-child-agent-sheet"
        data-oc-theme={agentBrand().themeId}
        data-design-system="opencloud-ui"
        side="right"
        {...(returnFocus ? { finalFocus: returnFocus } : {})}
      >
        <SheetHeader className="a3-child-agent-header">
          <p className="a3-eyebrow">Sub-agent</p>
          <SheetTitle>{purpose}</SheetTitle>
          <SheetDescription>
            {stateDetail}
          </SheetDescription>
          <div className="a3-child-agent-meta" aria-label="Sub-agent status">
            <Badge tone={childStateTone(part.data.state)}>{stateLabel}</Badge>
            <span>
              <Clock3 aria-hidden="true" />
              {childDuration(part.data.occurredAt, part.data.updatedAt)}
            </span>
          </div>
        </SheetHeader>

        <div className="a3-child-agent-body">
          {!inspection && summary}
          {loading && !inspection ? (
            <ChildAgentPanelState>
              <LoaderCircle className="a3-spin" aria-hidden="true" />
              <p>Loading activity…</p>
            </ChildAgentPanelState>
          ) : error && !inspection ? (
            <ChildAgentPanelState>
              <AlertCircle aria-hidden="true" />
              <p>{error}</p>
              <Button type="button" variant="outline" onClick={() => void load()}>
                <RefreshCw aria-hidden="true" /> Try again
              </Button>
            </ChildAgentPanelState>
          ) : inspection ? (
            <ChildAgentEvidence
              inspection={inspection}
              childRunId={part.data.runId}
              loadingMore={loadingMore}
              refreshError={error}
              summary={summary}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ChildAgentEvidence({
  inspection,
  childRunId,
  loadingMore = false,
  refreshError = null,
  summary,
}: {
  inspection: AgentTraceInspectionView;
  childRunId: string;
  loadingMore?: boolean;
  refreshError?: string | null;
  summary?: ReactNode;
}) {
  const entries = useMemo(
    () => inspection.entries.filter(({ runId }) => runId === childRunId),
    [childRunId, inspection.entries],
  );
  const projection = useMemo(
    () => projectChildAgentEvidence(entries),
    [entries],
  );

  return (
    <ScrollArea className="a3-child-agent-scroll">
      <div className="a3-child-agent-content">
        {summary}
        {refreshError ? (
          <Alert variant="destructive">
            <AlertCircle aria-hidden="true" />
            <AlertDescription>
              Showing the last loaded activity. {refreshError}
            </AlertDescription>
          </Alert>
        ) : null}

        {projection.timeline.length > 0 ? (
          <section
            className="a3-child-agent-timeline"
            aria-label="Sub-agent conversation and actions"
          >
            {projection.timeline.map((item) => (
              <ChildTimelineEntry key={item.id} item={item} />
            ))}
            {loadingMore ? (
              <div className="a3-child-agent-loading-more" role="status">
                <LoaderCircle className="a3-spin" aria-hidden="true" />
                Loading more activity…
              </div>
            ) : null}
          </section>
        ) : (
          <div className="a3-child-agent-empty">
            <Sparkles aria-hidden="true" />
            <strong>No messages or actions available yet.</strong>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

function ChildTimelineEntry({
  item,
}: {
  item: ChildAgentEvidenceTimelineItem;
}) {
  if (item.kind === "tools") {
    return (
      <ToolActivityCard
        calls={item.calls}
        active={item.active}
        defaultExpanded
      />
    );
  }
  if (item.kind === "reasoning") {
    return <ChildReasoningSummary text={item.text} streaming={item.streaming} />;
  }
  if (item.kind === "warning") {
    return (
      <Alert variant="destructive" className="a3-child-agent-warning">
        <AlertTriangle aria-hidden="true" />
        <AlertDescription>
          <strong>{item.title}</strong>
          <p>{item.detail}</p>
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <OpenCloudMessage align="start" data-child-message-phase={item.phase ?? undefined}>
      <MessageContent className="a3-turn-message-content">
        <Bubble className="a3-turn-bubble" align="start" variant="ghost">
          <BubbleContent className="a3-agent-turn">
            <Markdown className="a3-turn-copy">{item.text}</Markdown>
            {item.streaming ? <TypingIndicator /> : null}
          </BubbleContent>
        </Bubble>
      </MessageContent>
    </OpenCloudMessage>
  );
}

function ChildReasoningSummary({
  text,
  streaming,
}: {
  text: string;
  streaming: boolean;
}) {
  return (
    <Accordion
      className="a3-reasoning-summary"
      aria-label="Sub-agent reasoning summary"
      data-state={streaming ? "streaming" : "done"}
    >
      <AccordionItem value="reasoning">
        <AccordionTrigger>
          <span className="a3-reasoning-heading">
            <Sparkles aria-hidden="true" />
            <span>
              <strong>Reasoning summary</strong>
              <small>{streaming ? "Updating…" : "Shared by the sub-agent"}</small>
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <Markdown>{text}</Markdown>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function ChildAgentPanelState({ children }: { children: ReactNode }) {
  return <div className="a3-child-agent-state">{children}</div>;
}

function childEvidenceError(error: unknown, live: boolean): string {
  if (error instanceof AgentApiError && error.status === 404) {
    return live
      ? "Activity is not available yet. It will appear automatically."
      : "Activity is not available for this sub-agent.";
  }
  return error instanceof Error
    ? error.message
    : "The sub-agent’s activity could not be loaded.";
}

function childStateTone(
  state: ChildPart["data"]["state"],
): "success" | "warning" | "danger" | "neutral" {
  if (state === "succeeded") return "success";
  if (state === "failed") return "danger";
  if (state === "waiting") return "warning";
  return "neutral";
}

function childDuration(start: string, end: string): string {
  const duration = Date.parse(end) - Date.parse(start);
  if (!Number.isFinite(duration) || duration < 0) return "Duration unavailable";
  if (duration < 1_000) return `${duration}ms`;
  if (duration < 60_000) return `${Math.max(1, Math.round(duration / 1_000))}s`;
  const minutes = Math.floor(duration / 60_000);
  const seconds = Math.round((duration % 60_000) / 1_000);
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}
